// The new-question page's cards follow what the user is typing: the top row's cards (the ones
// drawn from the user's domains) become the questions that best match it, and go back when the
// box is cleared.
//
// Typed words match on every keystroke (settled briefly, so cards don't flicker mid-word). When
// typing pauses, Sayer's fast model names the text's subjects, related topics and domains, once
// per text: a question about a named subject comes first, and related topics reach questions
// that share a subject but not a word.
import { useEffect, useMemo, useRef, useState } from "react";
import { callLlm } from "../lib/forecast/llmClient.js";
import {
  buildIndex,
  parseRelated,
  queryTerms,
  relatedPrompt,
  relatedTerms,
  searchFeatured,
  subjectPhrases,
} from "../lib/featured/search.js";
import { TOP_ROW } from "../lib/featured/featured.js";
import { reportFailure } from "../lib/failureCounts.js";

const SETTLE_MS = 250;
const RELATED_PAUSE_MS = 700;
const RELATED_TIMEOUT_MS = 8000;
const SLOTS = TOP_ROW;

/** `value`, once it has stopped changing for `ms`. */
function useSettled(value, ms) {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return settled;
}

/**
 * @param {object} deps
 * @param {object[]} deps.cards the page's cards (pickFeatured)
 * @param {object[]} deps.pool every question the search may offer (already-owned ones removed)
 * @param {string} deps.text what is in the ask box
 * @returns {object[]} the cards to show; a matched one carries `related: true`
 */
export function useFeaturedSearch({ cards, pool, text }) {
  const index = useMemo(() => buildIndex(pool), [pool]);
  const domainNames = useMemo(
    () =>
      [
        ...new Set((Array.isArray(pool) ? pool : []).map((i) => i?.tags?.domain).filter(Boolean)),
      ].sort(),
    [pool],
  );
  const settledText = useSettled(text.trim(), SETTLE_MS);
  const pausedText = useSettled(text.trim(), RELATED_PAUSE_MS);

  // Related terms per text, kept for the page's life: going back to an earlier text costs nothing.
  const cache = useRef(new Map());
  const [related, setRelated] = useState({ text: "", value: null });
  useEffect(() => {
    if (queryTerms(pausedText).length === 0) return undefined;
    const hit = cache.current.get(pausedText);
    if (hit !== undefined) {
      const id = setTimeout(() => setRelated({ text: pausedText, value: hit }), 0);
      return () => clearTimeout(id);
    }
    const ctrl = new AbortController();
    callLlm(relatedPrompt(pausedText, domainNames), {
      webSearch: false,
      signal: ctrl.signal,
      timeoutMs: RELATED_TIMEOUT_MS,
    })
      .then((reply) => {
        const value = parseRelated(reply);
        cache.current.set(pausedText, value);
        if (!ctrl.signal.aborted) setRelated({ text: pausedText, value });
      })
      .catch((err) => {
        // Typed words still match without it. A request this effect cancelled (new text,
        // unmount) is not a failure; anything else is a lost suggestion, logged and counted.
        if (ctrl.signal.aborted) return;
        reportFailure("featured_search.related_failed", err);
      });
    return () => ctrl.abort();
  }, [pausedText, domainNames]);

  return useMemo(() => {
    const terms = queryTerms(settledText);
    // Related terms only while the text they were asked for is (the start of) what is typed.
    const rel = related.value && settledText.startsWith(related.text) ? related.value : null;
    const query = {
      terms,
      subjects: rel ? subjectPhrases(rel.subjects) : [],
      related: rel ? relatedTerms(rel.related, terms) : [],
      domains: new Set((rel?.domains || []).map((d) => d.toLowerCase())),
    };
    const base = Array.isArray(cards) ? cards : [];
    if (!terms.length) return base;
    // The cards that stay are never offered again in the matched slots.
    const kept = new Set(base.slice(SLOTS).map((c) => c.questionKey));
    const matches = searchFeatured(index, query, { limit: SLOTS, exclude: kept });
    if (!matches.length) return base;
    // Slot by slot, so a card that stays keeps its place: a match where there is one, else the
    // slot's own card (or, when that card is itself a match, the next one not shown).
    const matchKeys = new Set(matches.map((m) => m.questionKey));
    const used = new Set();
    const top = [];
    for (let i = 0; i < Math.min(SLOTS, base.length); i++) {
      if (i < matches.length) {
        top.push({ ...matches[i], related: true });
        continue;
      }
      const own = base[i];
      const pick =
        own && !matchKeys.has(own.questionKey) && !used.has(own.questionKey)
          ? own
          : base
              .slice(0, SLOTS)
              .find((c) => !matchKeys.has(c.questionKey) && !used.has(c.questionKey));
      if (pick) {
        used.add(pick.questionKey);
        top.push(pick);
      }
    }
    return [...top, ...base.slice(SLOTS)];
  }, [cards, index, settledText, related]);
}
